import Vue from 'vue';
import has from 'lodash/has';

import findAndSetFacet from './utils';

export default {
    /**
     * Turn off initial loading state of search machine
     * @param  {object} state
     */
    toggleLoading(state) {
        state.isLoading = false;
    },
    /**
     * Set searching state of search machine
     * @param  {object} state
     */
    setIsSearching(state, boolean) {
        state.isSearching = boolean;
    },
    /**
     * Update the keyword
     * @param  {object} state
     * @param  {string} keyword
     */
    updateKeyword(state, keyword) {
        state.keyword = keyword;
    },
    /**
     * Update the search results and related meta
     * Optionally appends to results array
     * @param  {object} state
     * @param  {array} results
     */
    updateResults(state, results) {
        state.results = results.data;
        state.currentPage = results.current_page;
        state.totalResults = results.total;
        state.totalPages = results.last_page;
    },
    /**
     * Sets available facets based on results of a search
     * @param  {object} state
     * @param  {object} data
     */
    updateAvailableFacets(state, data) {
        if (data.filter_counts) {
            state.availableFacets = data.filter_counts;
        } else {
            state.availableFacets = {};
        }
    },
    /**
     * Sets available types based on results of a search
     * @param  {object} state
     * @param  {object} data
     */
    updateAvailableTypes(state, data) {
        state.availableTypes = data.index_counts;
    },
    /**
     * Replace the applied facets with an updated set
     * @param  {object} state
     * @param  {object} data
     */
    replaceFacets(state, data) {
        if (data.length) {
            state.appliedFacets = data;
        } else {
            state.appliedFacets = [];
        }
        // Reset page count when adding a new facet
        state.currentPage = 1;
    },
    /**
     * Removes a facet from applied facets
     * @param  {object} state
     * @param  {object} facetToRemove
     */
    removeFacet(state, facetToRemove) {
        const facet = state.appliedFacets.findIndex((appliedFacet) => appliedFacet === facetToRemove);
        Vue.delete(state.appliedFacets, facet);
    },
    /**
     * Update the type specified for search
     * @param  {object} state
     * @param  {object} data
     */
    updateTypes(state, data) {
        if (data) {
            state.types = data;
        } else {
            state.types = [];
        }
    },
    goToPage(state, page) {
        state.currentPage = page;
    },
    /**
     * Reset current page
     * @param  {object} state
     */
    resetPageCount(state) {
        state.currentPage = 1;
    },
    /**
     * Set a forced mode for searches
     * @param  {object} state
     * @param  {string} mode
     */
    setMode(state, mode) {
        state.mode = mode;
    },
    /**
     * Reset applied facets and type
     * @param  {object} state
     */
    resetFacetsAndType(state) {
        state.appliedFacets = [];
        state.type = '';
        state.showSaved = false;
    },
    /**
     * Clears applied facets
     * @param  {object} state
     */
    clearFacets(state) {
        state.appliedFacets = [];
    },
    toggleShowSaved(state, value) {
        state.showSaved = value;
    },
    /**
     * Set an error message for caught excpetions
     * @param  {object} state
     * @param  {string} searchError
     */
    setSearchError(state, searchError) {
        state.searchError = searchError;
    },
    /**
     * Clear search error message
     * @param  {object} state
     */
    clearSearchError(state) {
        state.searchError = null;
    },

    /**
     * Sets initial state of search based on parsed url parameters
     * @param  {object} state
     * @param  {URLSearchParams} searchParams
     */
    setInitialState(state, searchParams) {
        if (Array.from(searchParams.entries()).length) {
            Vue.set(state, 'appliedFacets', []);
            state.type = '';
            Array.from(searchParams.entries()).forEach(([paramType, paramValue]) => {
                /**
                 * Keyword
                 */
                if (paramType === 'keyword') {
                    state.keyword = paramValue;
                }

                /**
                 * Mode
                 */
                if (paramType === 'mode') {
                    const modes = paramValue.split(',');
                    modes.forEach((mode) => {
                        state.types.push(mode);
                    });
                }

                /**
                 * Only saved items
                 */
                if (paramType === 'mysaved' && paramValue === '1') {
                    state.showSaved = paramValue;
                }

                /**
                 * Page
                 */
                if (paramType === 'page') {
                    state.currentPage = paramValue;
                }

                /**
                 * Facet Types
                 */
                const facetTypes = [
                    'department_id',
                    'article_type_id',
                    'resource_type_id',
                    'resource_category_id',
                    'audience_id',
                    'product_id',
                    'region_id',
                    'tag_id',
                    'topic_id',
                ];

                if (facetTypes.includes(paramType)) {
                    if (has(state.availableFacets, paramType)) {
                        findAndSetFacet(state, paramType, paramValue);
                    }
                }
            });
        }
    },
};
